import React, { useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { Box, Grid, Button, SectionTitle, Typography } from '@blueprint/ui';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { axiosInstance } from '../../../functions/axios';

import TableClientPagination from '../../Table/TableClientPagination';
import CustomerLogo from '../../CustomerLogo/CustomerLogo';
import { DatePickerQuickRange } from '@blueprint/ui';

const StyledGrid = styled(Grid)`
    padding-top: 100px;
    padding-right: 5vw;
    padding-left: 5vw;
    padding-bottom: 5em;

`;

const PaymentHistoryTable = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const [cursor, setCursor] = useState('default');
    const [paymentHistoryData, setPaymentHistoryData] = useState([]);
    const [dates, setDates] = useState({ fromDate: '', toDate: '' });
    const globalState = useSelector((state) => state);
    const emailId = useSelector((state) => state.beneDetails.details.emailId);
    const isRegisteredUser = useSelector((state) => state.beneDetails.registeredUser);
    const footerHeight = globalState.beneDetails.footerHeight;
    const PAYMENT_LIST = process.env.REACT_APP_PAYMENT_LIST;
    const ACCOUNT_HISTORY = process.env.REACT_APP_ACCOUNT_HISTORY;
    const FETCH_PAYMENT_HISTORY = process.env.REACT_APP_FETCH_PAYMENT_HISTORY;

    useEffect(() => {
        if (!globalState.beneDetails.details.isLogged) {
            history.push('/');
        }
    }, [globalState, history]);

    useEffect(() => {
        const fetchPaymentHistory = async () => {
            try {
                const response = await axiosInstance.post(
                    FETCH_PAYMENT_HISTORY,
                    JSON.stringify({
                        fromDate: dates.fromDate,
                        toDate: dates.toDate,
                    })
                );

                const responseJSON = response.data;
                console.log('Logging FETCH_PAYMENT_HISTORY response', responseJSON);
                setPaymentHistoryData(responseJSON);
            } catch (error) {
                console.log('Logging FETCH_PAYMENT_HISTORY error', error);
            }
        };
        fetchPaymentHistory();
    }, [FETCH_PAYMENT_HISTORY, dates]);

    const data = useMemo(() => paymentHistoryData, [paymentHistoryData]);
    const columns = useMemo(() => {
        const tableAccessorArray = [
            {
                accessor: 'custPaymentRef',
            },
            {
                accessor: 'emailId',
            },
            {
                accessor: 'customerName',
            },
            {
                accessor: 'instructedCurrency',
            },
            {
                accessor: 'instructedAmount',
            },
            {
                accessor: 'creditCurrency',
            },
            {
                accessor: 'creditAmount',
            },
            {
                accessor: 'fxRateApplied',
            },
            {
                accessor: 'creditAccount',
            },
            {
                accessor: 'creditBankDetails',
            },

            {
                accessor: 'paypalEmailId',
            },
            { accessor: 'status' },
            {
                accessor: 'sndrToRcvrInfo',
            },
            {
                accessor: 'entryDate',
            },
            {
                accessor: 'statusUpdateDt',
            },
        ];

        const accountHistoryColumns = tableAccessorArray.map((column) => {
            let columnDisplayText = t(`paymentHistory.${column.accessor}`);
            return { ...column, Header: columnDisplayText };
        });
        return accountHistoryColumns;
    }, [t]);

    const [fromErrorMessage, setFromErrorMessage] = useState('');
    const [toErrorMessage, setToErrorMessage] = useState('');
    const handleFromDateChange = (selectedDay, modifiers, dayPickerInput) => {
        const input = dayPickerInput.getInput();
        console.log('Formatted fromDate is', formatDate(selectedDay));
        let fromDate = formatDate(selectedDay);
        setDates({ ...dates, fromDate: fromDate });
        console.log('From:' + selectedDay);
        console.log('From input value:' + input.value);
        setFromErrorMessage('');
        if (!input.value.trim()) {
            setFromErrorMessage('Please type or pick from-date');
        }
        if (input.value.trim() && !selectedDay) {
            setFromErrorMessage('This from-date is invalid');
        }
    };

    const handleToDateChange = (selectedDay, modifiers, dayPickerInput) => {
        const input = dayPickerInput.getInput();
        console.log('Formatted toDate is', formatDate(selectedDay));
        let toDate = formatDate(selectedDay);
        setDates({ ...dates, toDate: toDate });
        console.log('To:' + selectedDay);
        console.log('To input value:' + input.value);

        setToErrorMessage('');
        if (!input.value.trim()) {
            setToErrorMessage('Please type or pick to-date');
        }
        if (input.value.trim() && !selectedDay) {
            setToErrorMessage('This to-date is invalid');
        }
    };

    const handleClickRecent = (range) => {
        console.log('From:' + range.from);
        console.log('To:' + range.to);
        let fromDate = formatDate(range.from);
        let toDate = formatDate(range.to);
        setDates({ fromDate: fromDate, toDate: toDate });
    };

    useEffect(() => console.log(dates), [dates]);

    const formatDate = (date) =>
        date.getFullYear() +
        '-' +
        (date.getMonth() < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) +
        '-' +
        (date.getDate() < 10 ? '0' + date.getDate() : date.getDate());

    return (
        <StyledGrid container role='main' style={{ paddingBottom: `${footerHeight + 20}px` }}>
            <CustomerLogo />
            <Grid item xs={12}>
                <Box>
                    <SectionTitle variant='h1' tabIndex='-1' marginBottom={4} id='payment-history-page' className='page-title'>
                        {t('paymentHistory.paymentHistory')}
                    </SectionTitle>
                </Box>
            </Grid>
            {isRegisteredUser ? (
                <Grid item xs={12}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm='auto'>
                            <Box marginBottom={['10px', '10px', '10px', '10px']}>
                                <Button
                                    id='button-payment-list'
                                    variant='light.tertiary'
                                    onClick={() => {
                                        history.push(PAYMENT_LIST);
                                    }}
                                >
                                    {t('paymentHistory.paymentList')}
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm='auto'>
                            <Box marginBottom={['10px', '10px', '10px', '10px']}>
                                <Button
                                    id='button-account-history'
                                    variant='light.tertiary'
                                    onClick={() => {
                                        history.push(ACCOUNT_HISTORY);
                                    }}
                                >
                                    {t('paymentHistory.accountHistory')}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            ) : null}
            {/*
                <Grid container>
                <Box marginTop={['8px', '8px', '8px', '8px']} marginBottom={['10px', '10px', '10px', '10px']} id='datepicker-quick'>
                    <DatePickerQuickRange
                        quickType='bottom'
                        handleFromDateChange={handleFromDateChange}
                        handleToDateChange={handleToDateChange}
                        handleClickRecent={handleClickRecent}
                    />
                </Box>
            </Grid>
            */}

            <Grid container>
                <TableClientPagination data={data} columns={columns} id='pl-table-paymentsList' />
            </Grid>
        </StyledGrid>
    );
};

export default PaymentHistoryTable;
